import { cn } from '@/helpers/ui-utils';
import { cva } from 'class-variance-authority';
import React from 'react';
import { TabsContent, TabsList, Tabs as TabsRoot, TabsTrigger } from './tabs';

export type Tab = {
  /**
   * Unique identifier for the tab. Used to associate triggers with content.
   */
  value: string;

  /**
   * Display text shown on the tab trigger button.
   */
  label: string;

  /**
   * React content to display when this tab is active.
   */
  content: React.ReactNode;

  /**
   * Whether this tab is disabled and cannot be selected.
   * @default false
   */
  disabled?: boolean;

  /**
   * Optional React node to display before the tab label (e.g., icons).
   */
  prefix?: React.ReactNode;

  /**
   * Optional React node to display after the tab label (e.g., badges, counts).
   */
  suffix?: React.ReactNode;
};

export type TabsProps = {
  /**
   * Visual style variant of the tabs component.
   * - `default` - Standard tabs with bottom border
   * - `pill` - Pill-shaped tabs with rounded background
   * @default 'default'
   */
  variant?: 'default' | 'pill';

  /**
   * Array of tab objects containing the configuration for each tab.
   */
  tabs: Tab[];

  /**
   * Optional description for the tab group, used for accessibility labeling.
   */
  description?: string;

  /**
   * Whether tabs should stretch to fill the available horizontal space.
   * @default false
   */
  stretch?: boolean;
} & React.ComponentProps<typeof TabsRoot>;

const tabsVariants = cva(['group/tab-root', 'gap-4', 'flex', 'flex-col'], {
  variants: {
    variant: {
      default: ['border-b', 'border-border', 'bg-transparent'],
      pill: [],
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const tabsListVariants = cva(
  [
    'group/tab-list',
    'bg-transparent',
    'grid',
    'auto-cols-max',
    'grid-flow-col',
  ],
  {
    variants: {
      variant: {
        default: ['border-b', 'border-border'],
      },
      stretch: {
        true: ['w-full', 'auto-cols-auto'],
        false: ['auto-cols-max'],
      },
    },
    defaultVariants: {
      variant: 'default',
      stretch: false,
    },
  },
);

const tabsTriggerVariants = cva(
  [
    'group/tab-trigger',
    'gap-2',
    'p-4',
    'text-foreground/80',
    'hover:text-foreground',
    'data-[state=active]:text-primary',
    'focus-visible:border-primary',
  ],
  {
    variants: {
      variant: {
        default: [
          'border-b-card',
          'hover:border-b-card-foreground/30',
          'data-[state=active]:border-b-primary',
        ],
        pill: [
          'bg-transparent',
          'rounded-[6px]',
          'border-0',
          'data-[state=active]:bg-[#FFF4F0]',
          'data-[state=active]:text-primary',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Tabs = React.forwardRef<React.ElementRef<typeof TabsRoot>, TabsProps>(
  ({ tabs, stretch, variant = 'default', className, ...props }, ref) => {
    return (
      <TabsRoot
        className={cn(tabsVariants({ variant }), className)}
        ref={ref}
        {...props}
      >
        <TabsList
          className={cn(tabsListVariants({ stretch }))}
          aria-label={props.description}
        >
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              disabled={tab.disabled}
              className={cn(tabsTriggerVariants({ variant }))}
            >
              {tab?.prefix}
              {tab.label}
              {tab?.suffix}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((tab) => (
          <TabsContent
            key={tab.value}
            value={tab.value}
            className={cn(['group/tab-content', 'm-0'])}
          >
            {tab.content}
          </TabsContent>
        ))}
      </TabsRoot>
    );
  },
);

export default Tabs;
